import { useState } from "react";
import { ChartColumn, File, Table } from "lucide-react";
import type { ViewMode } from "~/hooks/useViewMode";
import { cn } from "~/lib/utils";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import { ConversationProvider } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { ArtifactActionButtons } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactToolbar";
import { DownloadDialog } from "~/pages/manifold-nextgen/components/conversations/artifacts/DownloadDialog";
import { resourceIcon } from "~/pages/manifold-nextgen/components/resourceIcon";
import { isArtifactType } from "~/services/artifacts";
import { PinnedArtifact, pinRef, targetStep } from "~/services/pinnedArtifacts";
import { PinnedArtifactCard } from "./PinnedArtifactCard";
import { usePinnedArtifacts, usePinnedArtifactMutations } from "./usePinnedArtifacts";

interface PinnedArtifactsProps {
  projectId: string;
  // Whether the caller can share and unshare, which is owner-only. Refreshing
  // is open to every member.
  canCurate: boolean;
  view: ViewMode;
  // The pin whose lineage the page's panel shows, if any.
  selectedPinId: string | null;
  onOpenLineage: (pinId: string) => void;
  // The name of a session the caller can see, or null for another member's.
  sessionName: (conversationId: string) => string | null;
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

// The project's shared artifacts, rendered for every member as cards or rows.
export const PinnedArtifacts = ({
  projectId,
  canCurate,
  view,
  selectedPinId,
  onOpenLineage,
  sessionName,
}: PinnedArtifactsProps) => {
  const { pinnedArtifacts, error } = usePinnedArtifacts(projectId);
  const { unpin } = usePinnedArtifactMutations(projectId);

  if (error) {
    return (
      <StatusMessage
        title="Couldn't load shared artifacts"
        body="Something went wrong. Try again in a moment."
        icon={resourceIcon("artifacts")}
        className="py-16"
      />
    );
  }
  if (!pinnedArtifacts) return null;
  if (pinnedArtifacts.length === 0) {
    return (
      <StatusMessage
        variant="empty"
        title="No shared artifacts yet"
        body={
          canCurate
            ? "Use the share icon on a chart or table in a session."
            : "Project owners can share charts and tables from a session."
        }
        icon={resourceIcon("artifacts")}
        className="py-16"
      />
    );
  }

  const actionsFor = (pin: PinnedArtifact) => ({
    lineage: { isOpen: pin.id === selectedPinId, open: () => onOpenLineage(pin.id) },
    share: canCurate ? { isShared: true, isReady: true, toggle: () => void unpin(pin) } : null,
  });

  if (view === "list") {
    return (
      <div className="overflow-hidden rounded-lg border border-border" role="table">
        <div
          role="row"
          className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_minmax(0,1.2fr)_90px_120px] items-center gap-3 border-b border-border bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground"
        >
          <span role="columnheader">Name</span>
          <span role="columnheader">Session</span>
          <span role="columnheader">Shared by</span>
          <span role="columnheader">Updated</span>
          <span role="columnheader" className="sr-only">
            Actions
          </span>
        </div>
        {pinnedArtifacts.map((pin) => (
          <PinnedArtifactRow
            key={pin.id}
            pin={pin}
            isSelected={pin.id === selectedPinId}
            session={sessionName(pin.source_conversation_id)}
            actions={actionsFor(pin)}
            onOpen={() => onOpenLineage(pin.id)}
          />
        ))}
      </div>
    );
  }

  return (
    // Two-up only from xl: a chart narrower than ~350px renders as a "view in
    // fullscreen" placeholder instead of the chart.
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
      {pinnedArtifacts.map((pin) => (
        <PinnedArtifactCard
          key={pin.id}
          pin={pin}
          canCurate={canCurate}
          onUnpin={() => void unpin(pin)}
          isLineageOpen={pin.id === selectedPinId}
          onOpenLineage={() => onOpenLineage(pin.id)}
        />
      ))}
    </div>
  );
};

const TypeIcon = ({ artifactType }: { artifactType: string | undefined }) => {
  if (artifactType === "figure") return <ChartColumn className="size-4" />;
  if (artifactType === "dataframe") return <Table className="size-4" />;
  return <File className="size-4" />;
};

interface PinnedArtifactRowProps {
  pin: PinnedArtifact;
  isSelected: boolean;
  session: string | null;
  actions: Parameters<typeof ArtifactActionButtons>[0]["actions"];
  onOpen: () => void;
}

const PinnedArtifactRow = ({
  pin,
  isSelected,
  session,
  actions,
  onOpen,
}: PinnedArtifactRowProps) => {
  const target = targetStep(pin);
  const [downloadOpen, setDownloadOpen] = useState(false);
  // The row shows no card, so the format comes from the recipe's target step.
  const downloadType =
    target && isArtifactType(target.artifact_type) ? target.artifact_type : undefined;
  return (
    <div
      role="row"
      className={cn(
        "grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_minmax(0,1.2fr)_90px_120px] items-center gap-3 border-b border-border/60 px-3 py-2.5 last:border-b-0 hover:bg-muted/40",
        isSelected && "bg-muted/60"
      )}
    >
      <span role="cell" className="min-w-0">
        <button
          type="button"
          onClick={onOpen}
          className="flex w-full min-w-0 cursor-pointer items-center gap-2.5 text-left"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
            <TypeIcon artifactType={target?.artifact_type} />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium">{pin.display_name}</span>
            {target?.summary && (
              <span className="truncate text-xs text-muted-foreground">{target.summary}</span>
            )}
          </span>
        </button>
      </span>
      <span role="cell" className="truncate text-[13px] text-muted-foreground">
        {session ?? "Another member's session"}
      </span>
      <span
        role="cell"
        className="truncate text-[13px] text-muted-foreground"
        title={pin.created_by}
      >
        {pin.created_by}
      </span>
      <span role="cell" className="text-[13px] text-muted-foreground">
        {shortDate(pin.updated_at)}
      </span>
      <div role="cell" className="flex justify-end">
        {/* No session is in scope on this page: the row downloads through its
            pin, as the card does. */}
        <ConversationProvider conversationId={null} pin={pinRef(pin)}>
          <ArtifactActionButtons
            actions={actions}
            onDownload={downloadType && (() => setDownloadOpen(true))}
          />
          {downloadType && downloadOpen && (
            <DownloadDialog
              open
              onClose={() => setDownloadOpen(false)}
              artifactType={downloadType}
            />
          )}
        </ConversationProvider>
      </div>
    </div>
  );
};
